import PageMeta from "@/components/PageMeta";
import SectionFadeIn from "@/components/SectionFadeIn";
import { Shield } from "lucide-react";

const LAST_UPDATED = "June 2026";

const SECTIONS = [
  {
    id: "who-we-are",
    title: "1. Who We Are",
    body: [
      "AM Decor Signs & Prints (\u201cwe\u201d, \u201cus\u201d, \u201cour\u201d) is a print and signage business based in Coleraine, Northern Ireland. We provide canvas prints, wall murals, stickers and labels, banners, vehicle graphics, and related custom print services.",
      "This Privacy Policy explains how we collect, use, and protect your personal information when you use our website or place an order or quote request with us. We are the \u201cdata controller\u201d responsible for your personal data under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.",
    ],
  },
  {
    id: "information-we-collect",
    title: "2. Information We Collect",
    body: [
      "We only collect information that we need to respond to your enquiries and fulfil your orders. This may include:",
    ],
    list: [
      "Contact details such as your name, email address, phone number, and delivery/billing address.",
      "Order and quote details, including product specifications, dimensions, artwork, and any files you upload.",
      "Payment information processed securely by our payment provider (we do not store full card details ourselves).",
      "Communications you send us via our website forms, email, WhatsApp, or phone.",
      "Technical information such as your IP address, browser type, and basic usage data collected automatically for security and performance.",
    ],
  },
  {
    id: "how-we-use",
    title: "3. How We Use Your Information",
    body: ["We use your personal information to:"],
    list: [
      "Prepare quotes and process and fulfil your orders.",
      "Communicate with you about your enquiry, order, or delivery.",
      "Provide customer support and respond to your questions.",
      "Take payment and, where necessary, arrange refunds.",
      "Meet our legal, accounting, and tax obligations.",
      "Improve our website, products, and services.",
    ],
  },
  {
    id: "legal-basis",
    title: "4. Legal Basis for Processing",
    body: [
      "We process your personal data on the following legal bases: to perform our contract with you (processing your order or quote), to comply with our legal obligations (such as tax and accounting records), and for our legitimate interests (such as running and improving our business and keeping our website secure). Where we rely on consent \u2014 for example for optional marketing \u2014 you can withdraw it at any time.",
    ],
  },
  {
    id: "sharing",
    title: "5. Sharing Your Information",
    body: [
      "We do not sell your personal information. We only share it with trusted third parties who help us run our business, including:",
    ],
    list: [
      "Payment processors to take payment securely.",
      "Delivery and courier partners to ship your order.",
      "Email and hosting service providers that operate our website and communications.",
      "Professional advisers and authorities where required by law.",
    ],
  },
  {
    id: "retention",
    title: "6. Data Retention",
    body: [
      "We keep your personal information only for as long as necessary to fulfil the purposes we collected it for, including to satisfy any legal, accounting, or reporting requirements. Order and transaction records are typically retained for a minimum of six years in line with UK tax law. When data is no longer needed, we securely delete or anonymise it.",
    ],
  },
  {
    id: "your-rights",
    title: "7. Your Rights",
    body: ["Under UK data protection law, you have the right to:"],
    list: [
      "Access the personal data we hold about you.",
      "Request correction of inaccurate or incomplete data.",
      "Request erasure of your data where there is no legal reason to keep it.",
      "Object to or restrict our processing of your data.",
      "Request a copy of your data in a portable format.",
      "Withdraw consent at any time where processing is based on consent.",
    ],
  },
  {
    id: "cookies",
    title: "8. Cookies",
    body: [
      "Our website uses essential cookies to function correctly and may use limited analytics to understand how visitors use the site so we can improve it. You can control or delete cookies through your browser settings. Disabling essential cookies may affect how parts of the site work.",
    ],
  },
  {
    id: "security",
    title: "9. Security",
    body: [
      "We take appropriate technical and organisational measures to protect your personal information against unauthorised access, loss, or misuse. Payments are handled by secure, PCI-compliant payment providers, and access to your data within our business is limited to those who need it.",
    ],
  },
  {
    id: "contact",
    title: "10. Contact Us",
    body: [
      "If you have any questions about this Privacy Policy, wish to exercise your rights, or want to make a complaint about how we handle your data, please contact us:",
    ],
    list: [
      "Email: hello@amdecorsignsandprints.co.uk",
      "Phone: Adrian 07597 650510 / Matthew 07947 439865",
      "Address: Coleraine, Northern Ireland",
    ],
    footer:
      "You also have the right to lodge a complaint with the UK Information Commissioner\u2019s Office (ICO) at ico.org.uk if you believe your data has been handled improperly.",
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="bg-brand-bg min-h-screen" data-testid="privacy-policy-page">
      <PageMeta
        title="Privacy Policy"
        description="How AM Decor Signs & Prints collects, uses, and protects your personal information under UK GDPR."
      />

      <section className="max-w-3xl mx-auto px-6 md:px-8 pt-24 pb-20">
        <SectionFadeIn>
          <div className="flex items-center gap-3 mb-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand-gold/12 text-brand-gold">
              <Shield className="h-5 w-5" />
            </span>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-brand-gold">
              Legal
            </span>
          </div>
          <h1
            className="text-4xl sm:text-5xl font-semibold text-brand-text tracking-tight"
            data-testid="privacy-policy-heading"
          >
            Privacy Policy
          </h1>
          <p className="mt-4 text-sm text-brand-text-secondary/70">
            Last updated: {LAST_UPDATED}
          </p>
          <p className="mt-6 text-base text-brand-text-secondary leading-relaxed">
            Your privacy matters to us. This policy explains what personal
            information we collect, how we use it, and the rights you have over
            your data.
          </p>
        </SectionFadeIn>

        <div className="mt-14 space-y-12">
          {SECTIONS.map((section) => (
            <SectionFadeIn key={section.id}>
              <div data-testid={`privacy-section-${section.id}`}>
                <h2 className="text-lg font-semibold text-brand-text mb-4">
                  {section.title}
                </h2>
                {section.body?.map((para, i) => (
                  <p
                    key={i}
                    className="text-sm text-brand-text-secondary leading-relaxed mb-3"
                  >
                    {para}
                  </p>
                ))}
                {section.list && (
                  <ul className="mt-2 space-y-2">
                    {section.list.map((item, i) => (
                      <li
                        key={i}
                        className="flex gap-3 text-sm text-brand-text-secondary leading-relaxed"
                      >
                        <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-brand-gold" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {section.footer && (
                  <p className="mt-4 text-sm text-brand-text-secondary/80 leading-relaxed">
                    {section.footer}
                  </p>
                )}
              </div>
            </SectionFadeIn>
          ))}
        </div>
      </section>
    </div>
  );
}
